import { Component, inject } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { StorageService } from '../../services/storage';
import { ConfirmService } from '../../services/confirm.service';
import { formatBytes } from '../../core/utils/format-bytes';

/**
 * Standalone Speicher-Status-Indikator.
 * Zeigt Speicher-Status mit Overlay-Icons:
 * - Rot: Speicher knapp
 * - Grüner Haken: Persistent
 * - Orange Warnung: Nicht persistent
 */
@Component({
	selector: 'wd-storage-status',
	standalone: true,
	imports: [NgbTooltipModule],
	template: `
		<button class="btn btn-status" placement="start"
			[ngbTooltip]="storage.storageMessage()" tooltipClass="custom-tooltip-class"
			(click)="onStorageClick()">
			@if (storage.isStorageLow) {
				<div class="icon-overlay">
					<i class="bi bi-database base-icon" style="color: red"></i>
					<i class="bi bi-exclamation-triangle-fill overlay-icon" style="color: red"></i>
				</div>
			} @else if (storage.isPersistentStorage) {
				<div class="icon-overlay">
					<i class="bi bi-database base-icon"></i>
					<i class="bi bi-check overlay-icon" style="color: green"></i>
				</div>
			} @else {
				<div class="icon-overlay">
					<i class="bi bi-database base-icon"></i>
					<i class="bi bi-exclamation-triangle-fill overlay-icon" style="color: orange"></i>
				</div>
			}
		</button>
	`,
	styles: [`
		.btn-status { padding-left: 0.1rem; border-style: none; }
		.icon-overlay { position: relative; }
		.overlay-icon { position: absolute; bottom: -5px; right: -5px; font-size: 12px; }
	`]
})
export class WdStorageStatusComponent {
	storage = inject(StorageService);
	private confirmService = inject(ConfirmService);

	async onStorageClick(): Promise<void> {
		const estimate = this.storage.storageEstimate;
		const used = formatBytes(estimate?.usage ?? 0);
		const quota = formatBytes(estimate?.quota ?? 0);
		const percent = this.storage.storageUsagePercent;

		if (this.storage.isStorageLow) {
			await this.confirmService.alert(
				`Der Speicher wird knapp!\n\n` +
				`Aktueller Speicherverbrauch: ${used} von ${quota} (${percent}%)\n\n` +
				`Empfehlungen:\n` +
				`\u2022 Nicht mehr benötigte Offline-Projekte löschen\n` +
				`\u2022 Browser-Cache leeren\n` +
				`\u2022 Alte Kartendaten entfernen\n\n` +
				`Bei vollem Speicher können keine weiteren Daten heruntergeladen werden.`,
				'Speicher knapp'
			);
			return;
		}

		if (this.storage.isPersistentStorage) {
			return;
		}

		if (navigator.storage?.persist) {
			const granted = await navigator.storage.persist();
			this.storage.isPersistentStorage = granted;
			this.storage.updateStorageInfo();

			if (!granted) {
				await this.confirmService.alert(
					`Der Browser hat persistenten Speicher nicht gewährt.\n\n` +
					`Aktueller Speicherverbrauch: ${used} von ${quota} (${percent}%)\n\n` +
					`Mögliche Lösungen:\n` +
					`\u2022 App zum Startbildschirm hinzufügen (PWA installieren)\n` +
					`\u2022 Seite als Lesezeichen speichern\n` +
					`\u2022 Benachrichtigungen erlauben\n\n` +
					`Ohne persistenten Speicher können Offline-Daten bei Speichermangel verloren gehen.`,
					'Speicher nicht persistent'
				);
			}
		}
	}
}
